import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { SimpleCard } from 'components/cards/SimpleCard';
import { ErrorMessage } from 'components/ErrorMessage';
import { SuspendWithRelativelyCenteredSpinner } from 'components/suspense/SuspendWithRelativelyCenteredSpinner';
import { OverlayProps } from 'components/TaskOverlay';
import React from 'react';
import { ErrorBoundary } from 'utils/errors';
import { graphql, useSafeRelay } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { VaultFieldInput } from '../VaultFieldInput';
import { ManageVaultFieldsModal_vaultManager_Query } from './__generated__/ManageVaultFieldsModal_vaultManager_Query.graphql';

const vaultManagerQuery = graphql`
  query ManageVaultFieldsModal_vaultManager_Query {
    vaultManager {
      id
      availableFields {
        edges {
          node {
            id
            name
          }
        }
      }
      ...VaultFieldInput_vaultManager
    }
  }
`;

interface ManageVaultFieldsModalProps extends OverlayProps {
  vaultId: string;
}

export const ManageVaultFieldsModal = ({
  isOpen,
  onClose,
  vaultId,
}: ManageVaultFieldsModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ErrorBoundary fallback={<ErrorFallback onClose={onClose} />}>
        <SuspendWithRelativelyCenteredSpinner>
          <UnsuspendedManageVaultFieldsModalContent
            onClose={onClose}
            vaultId={vaultId}
          />
        </SuspendWithRelativelyCenteredSpinner>
      </ErrorBoundary>
    </Modal>
  );
};

export const UnsuspendedManageVaultFieldsModalContent = ({
  onClose,
  vaultId,
}: Pick<ManageVaultFieldsModalProps, 'onClose' | 'vaultId'>) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { vaultManager } =
    useLazyLoadQuery<ManageVaultFieldsModal_vaultManager_Query>(
      vaultManagerQuery,
      {},
    );

  const availableFields = vaultManager.availableFields.edges
    .map((edge) => edge.node)
    .filter(notEmpty);

  return (
    <ModalContent>
      <ManageVaultFieldsModalHeader />
      <ModalBody>
        <Stack spacing={4}>
          <Text>{t`Vault Field help you organise and find Vaults faster. The fields can also be used by the Vault Distributor to automatically assign files to their correct vault.`}</Text>

          <Stack spacing={6}>
            {availableFields.map((availableField) => (
              <Stack key={availableField.id} spacing={2}>
                <Heading size="md">{availableField.name}</Heading>
                <VaultFieldInput
                  vaultId={vaultId}
                  fieldId={availableField.id}
                  vaultManager={vaultManager}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button size="sm" onClick={onClose}>{t`Close`}</Button>
      </ModalFooter>
    </ModalContent>
  );
};

const ErrorFallback = ({
  onClose,
}: Pick<ManageVaultFieldsModalProps, 'onClose'>) => {
  return (
    <ModalContent>
      <ManageVaultFieldsModalHeader />
      <ModalBody>
        <SimpleCard width="100%">
          <ErrorMessage
            size="lg"
            title={t`Error loading vault manager information.`}
          />
        </SimpleCard>
      </ModalBody>
      <ModalFooter>
        <Button size="sm" onClick={onClose}>{t`Cancel`}</Button>
      </ModalFooter>
    </ModalContent>
  );
};

const ManageVaultFieldsModalHeader = () => (
  <ModalHeader>{t`Manage vault fields`}</ModalHeader>
);
